import moment from "moment";

const ROW_DATE_KEYS = [
  "transactionDate",
  "TransactionDate",
  "transactionDateTime",
  "TransactionDateTime",
  "createdDate",
  "CreatedDate",
  "date",
  "Date",
];

/** Parse "(UTC+05:00) Islamabad, Karachi" -> offset minutes east of UTC */
export function parseUtcOffsetMinutes(utcTimeZoneLabel) {
  if (!utcTimeZoneLabel || typeof utcTimeZoneLabel !== "string") return null;
  const match = utcTimeZoneLabel.match(/UTC([+-])(\d{2}):(\d{2})/i);
  if (!match) return null;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

function formatOffsetMinutes(minutes) {
  const sign = minutes >= 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const h = String(Math.floor(abs / 60)).padStart(2, "0");
  const m = String(abs % 60).padStart(2, "0");
  return `${sign}${h}:${m}`;
}

function isValidIanaTimeZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function parseToMoment(dateValue) {
  if (dateValue == null || dateValue === "") return null;
  if (moment.isMoment(dateValue)) return dateValue.isValid() ? dateValue : null;
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) return moment(dateValue);

  const direct = moment(dateValue);
  if (direct.isValid()) return direct;

  const raw = String(dateValue).trim();
  const naive = moment(raw, [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss"], true);
  return naive.isValid() ? naive : null;
}

/**
 * Format a company-local SQL datetime for display.
 * compTimeZone is often a Windows StandardName (invalid for Intl); compUTCTimeZone holds the UTC offset label.
 */
export function formatCompanyDateTime(dateValue, { compTimeZone, compUTCTimeZone } = {}) {
  const m = parseToMoment(dateValue);
  if (!m) return "-";

  const ianaTz = isValidIanaTimeZone(compTimeZone) ? compTimeZone : null;
  if (ianaTz) {
    try {
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: ianaTz,
      }).format(m.toDate());
    } catch {
      // fall through
    }
  }

  const offsetMin = parseUtcOffsetMinutes(compUTCTimeZone);
  if (offsetMin != null) {
    const wall = m.format("YYYY-MM-DDTHH:mm:ss");
    return moment.parseZone(`${wall}${formatOffsetMinutes(offsetMin)}`).format("MMM D, YYYY h:mm A");
  }

  return m.format("MMM D, YYYY h:mm A");
}

export function getRowDateValue(row) {
  if (!row) return null;
  for (const key of ROW_DATE_KEYS) {
    const value = row[key];
    if (value == null || value === "") continue;
    const m = parseToMoment(value);
    if (m) return m;
  }
  return null;
}

export function formatLedgerRowDateTime(row, timezoneOptions = {}) {
  const m = getRowDateValue(row);
  if (!m) return "-";
  return formatCompanyDateTime(m, timezoneOptions);
}

export function getRowDateTimestamp(row) {
  const m = getRowDateValue(row);
  return m ? m.valueOf() : 0;
}

/** Wall-clock parts for "now" in the company timezone. */
export function getCompanyNowParts({ compTimeZone, compUTCTimeZone } = {}) {
  const now = new Date();
  const ianaTz = isValidIanaTimeZone(compTimeZone) ? compTimeZone : null;
  if (ianaTz) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute") };
  }
  const offsetMin = parseUtcOffsetMinutes(compUTCTimeZone);
  if (offsetMin != null) {
    const wall = moment.utc(now.getTime() + offsetMin * 60 * 1000);
    return {
      year: wall.format("YYYY"),
      month: wall.format("MM"),
      day: wall.format("DD"),
      hour: wall.format("HH"),
      minute: wall.format("mm"),
    };
  }
  const m = moment(now);
  return { year: m.format("YYYY"), month: m.format("MM"), day: m.format("DD"), hour: m.format("HH"), minute: m.format("mm") };
}

/** `YYYY-MM-DDTHH:mm` for datetime-local inputs (company wall clock). */
export function getCompanyDateTimeLocalValue(dateValue, timezoneOptions = {}) {
  if (dateValue == null || dateValue === "") {
    const p = getCompanyNowParts(timezoneOptions);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  }
  if (moment.isMoment(dateValue) && dateValue.isValid()) {
    return dateValue.format("YYYY-MM-DDTHH:mm");
  }

  const instant =
    dateValue instanceof Date
      ? dateValue
      : typeof dateValue === "string"
        ? new Date(dateValue)
        : null;
  if (!instant || isNaN(instant.getTime())) {
    const p = getCompanyNowParts(timezoneOptions);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  }

  const { compTimeZone, compUTCTimeZone } = timezoneOptions;
  const ianaTz = isValidIanaTimeZone(compTimeZone) ? compTimeZone : null;
  if (ianaTz) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  }

  const offsetMin = parseUtcOffsetMinutes(compUTCTimeZone);
  if (offsetMin != null) {
    return moment.utc(instant.getTime() + offsetMin * 60 * 1000).format("YYYY-MM-DDTHH:mm");
  }

  return moment(instant).format("YYYY-MM-DDTHH:mm");
}

/** Parse datetime-local value as company wall time → JavaScript Date (UTC instant). */
export function parseCompanyDateTimeLocal(localValue, { compTimeZone, compUTCTimeZone } = {}) {
  if (!localValue || typeof localValue !== "string") return null;
  const normalized = localValue.length === 16 ? `${localValue}:00` : localValue;
  const offsetMin = parseUtcOffsetMinutes(compUTCTimeZone);
  if (offsetMin != null) {
    const parsed = moment.parseZone(`${normalized}${formatOffsetMinutes(offsetMin)}`);
    return parsed.isValid() ? parsed.toDate() : null;
  }
  const ianaTz = isValidIanaTimeZone(compTimeZone) ? compTimeZone : null;
  if (ianaTz) {
    try {
      const utcGuess = Date.UTC(
        parseInt(normalized.slice(0, 4), 10),
        parseInt(normalized.slice(5, 7), 10) - 1,
        parseInt(normalized.slice(8, 10), 10),
        parseInt(normalized.slice(11, 13), 10) || 0,
        parseInt(normalized.slice(14, 16), 10) || 0,
        0
      );
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: ianaTz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      let guess = utcGuess;
      for (let i = 0; i < 4; i += 1) {
        const parts = fmt.formatToParts(new Date(guess));
        const get = (type) => parts.find((p) => p.type === type)?.value;
        const wall = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
        if (wall === localValue.slice(0, 16)) return new Date(guess);
        const want = Date.parse(`${localValue.slice(0, 16)}:00Z`);
        const got = Date.parse(`${wall}:00Z`);
        guess += want - got;
      }
      return new Date(guess);
    } catch {
      // fall through
    }
  }
  const parsed = moment(localValue, "YYYY-MM-DDTHH:mm", true);
  return parsed.isValid() ? parsed.toDate() : null;
}

/** Current instant; end-of-range uses company "now" (same universal instant). */
export function getCompanyNowDate() {
  return new Date();
}

export function formatCompanyDateTimeLocalDisplay(dateValue, timezoneOptions = {}) {
  return formatCompanyDateTime(dateValue, timezoneOptions);
}

/** API payload: naive company wall clock (no timezone offset). */
export function toCompanyWallClockDateTimeString(momentValue) {
  if (!momentValue || !moment.isMoment(momentValue) || !momentValue.isValid()) return null;
  return momentValue.format("YYYY-MM-DDTHH:mm:ss");
}

/** Format any date value as company wall-clock string for report/API params. */
export function formatCompanyDateTimeForApi(dateValue, timezoneOptions = {}) {
  const local = getCompanyDateTimeLocalValue(dateValue, timezoneOptions);
  if (!local) return null;
  return local.length === 16 ? `${local}:00` : local;
}

/**
 * Parse API / SQL datetime into a moment for date-time pickers (company wall clock).
 * Strips UTC markers so values are not shifted by the browser timezone.
 */
export function parseCompanyDateTimeForPicker(dateValue, { compTimeZone, compUTCTimeZone } = {}) {
  if (dateValue == null || dateValue === "") return null;
  if (moment.isMoment(dateValue) && dateValue.isValid()) return dateValue;
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    const localStr = getCompanyDateTimeLocalValue(dateValue, { compTimeZone, compUTCTimeZone });
    const parsed = parseCompanyDateTimeLocal(localStr, { compTimeZone, compUTCTimeZone });
    return parsed ? moment(parsed) : moment(dateValue);
  }

  let raw = String(dateValue).trim();
  raw = raw.replace(/Z$/i, "").replace(/[+-]\d{2}:\d{2}(?::\d{2})?$/, "");
  const naive = moment(raw, ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSS"], true);
  if (!naive.isValid()) {
    const fallback = parseToMoment(dateValue);
    return fallback;
  }

  const wall = naive.format("YYYY-MM-DDTHH:mm:ss");
  const offsetMin = parseUtcOffsetMinutes(compUTCTimeZone);
  if (offsetMin != null) {
    return moment.parseZone(`${wall}${formatOffsetMinutes(offsetMin)}`);
  }

  const ianaTz = isValidIanaTimeZone(compTimeZone) ? compTimeZone : null;
  if (ianaTz) {
    const localStr = naive.format("YYYY-MM-DDTHH:mm");
    const parsed = parseCompanyDateTimeLocal(localStr, { compTimeZone, compUTCTimeZone });
    return parsed ? moment(parsed) : naive;
  }

  return naive;
}
